import React, { useState } from "react";
import { Modal, Button, Form, Alert } from "react-bootstrap";
import { AlertTriangle, AlertCircle } from "lucide-react";
import { switchTeacherSchool } from "../services/APIClient";

function SwitchSchoolAccessModal({ show, onHide, teacherId, currentSchoolId, schoolId, schoolName }) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Confirmation checkboxes
  const [confirmations, setConfirmations] = useState({
    noClasses: false,
    understand: false,
    notAbuse: false,
  });

  const allConfirmed = Object.values(confirmations).every((v) => v === true);
  const isFormValid = schoolId && allConfirmed;

  const handleConfirmationChange = (e) => {
    const { name, checked } = e.target;
    setConfirmations({
      ...confirmations,
      [name]: checked,
    });
  };

  const handleSwitchSchool = async () => {
    if (!isFormValid) {
      setError("Please complete all confirmations before proceeding");
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      await switchTeacherSchool(teacherId, schoolId);
      setSuccess(true);
      
      // Close modal after 2 seconds to show success message
      setTimeout(() => {
        handleClose();
      }, 2000);
    } catch (err) {
      setError(err.message || "Failed to switch school. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    // Reset form
    setConfirmations({
      noClasses: false,
      understand: false,
      notAbuse: false,
    });
    setError(null);
    setSuccess(false);
    onHide();
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg" centered backdrop="static">
      <Modal.Header closeButton>
        <Modal.Title className="d-flex align-items-center gap-2">
          <AlertTriangle size={24} style={{ color: "#dc2626" }} />
          Temporary School Access
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        {success ? (
          <Alert variant="success" className="mb-0">
            <strong>✓ Success!</strong>
            <p className="mb-0 mt-2">
              You have been switched to the school. You will be logged out and need to log back in.
            </p>
          </Alert>
        ) : (
          <>
            {/* CRITICAL WARNINGS */}
            <Alert variant="danger" className="mb-4">
              <div className="d-flex gap-2">
                <AlertTriangle size={20} className="flex-shrink-0 mt-1" />
                <div>
                  <strong>⚠️ CRITICAL WARNINGS</strong>
                  <p className="mb-2 mt-2">
                    You are about to temporarily access another school's environment. This is a <strong>PROOF OF CONCEPT</strong> feature.
                  </p>
                  <p className="mb-0">
                    <strong>This is a LAST RESORT option</strong> to help teachers experiencing issues. <strong>Abuse of this feature will be monitored and addressed.</strong>
                  </p>
                </div>
              </div>
            </Alert>

            {/* WHAT THIS MEANS */}
            <Alert variant="warning" className="mb-4">
              <strong>What This Does:</strong>
              <ul className="mb-0 mt-2">
                <li>You will see the school environment exactly as their teachers see it</li>
                <li>You will have full access to their classes and data</li>
                <li>Your account will be transferred to their school temporarily</li>
                <li>This access is for diagnostic purposes only</li>
              </ul>
            </Alert>

            {/* NON-NEGOTIABLE REQUIREMENTS */}
            <Alert variant="danger" className="mb-4">
              <strong>Non-Negotiable Requirements:</strong>
              <ol className="mb-0 mt-2">
                <li>
                  <strong>You must have 0 classes assigned to your account</strong> before switching
                </li>
                <li>
                  <strong>You must have 0 shared classes</strong> with your account before switching
                </li>
                <li>
                  <strong>You MUST switch back immediately after completing your diagnosis</strong>
                </li>
                <li>
                  <strong>You MUST add yourself back to the ALPACA school</strong> when done
                </li>
              </ol>
              <p className="mb-0 mt-3 text-danger">
                <strong>Failure to comply will result in disciplinary action.</strong>
              </p>
            </Alert>

            {error && <Alert variant="danger">{error}</Alert>}

            {/* SELECTED SCHOOL DISPLAY */}
            <div className="mb-4 p-3" style={{ backgroundColor: '#f3f4f6', borderRadius: '0.375rem', border: '1px solid #e5e7eb' }}>
              <strong className="d-block mb-2">School to Access:</strong>
              <p className="mb-1">{schoolName}</p>
              <Form.Text className="text-muted">ID: {schoolId}</Form.Text>
              {currentSchoolId && (
                <>
                  <hr style={{ margin: '1rem 0' }} />
                  <strong className="d-block mb-2">Your Current School:</strong>
                  <Form.Text className="text-muted">ID: {currentSchoolId}</Form.Text>
                </>
              )}
            </div>

            {/* CONFIRMATION CHECKBOXES */}
            <div className="mb-4">
              <strong className="d-block mb-3">Please confirm:</strong>

              <Form.Check
                type="checkbox"
                id="confirmNoClasses"
                name="noClasses"
                label={
                  <span>
                    I confirm that I have <strong>0 classes</strong> assigned to my account and{" "}
                    <strong>0 shared classes</strong>
                  </span>
                }
                checked={confirmations.noClasses}
                onChange={handleConfirmationChange}
                disabled={isLoading}
                className="mb-3"
              />

              <Form.Check
                type="checkbox"
                id="confirmUnderstand"
                name="understand"
                label={
                  <span>
                    I understand this is a <strong>temporary</strong> access for diagnostic purposes only
                  </span>
                }
                checked={confirmations.understand}
                onChange={handleConfirmationChange}
                disabled={isLoading}
                className="mb-3"
              />

              <Form.Check
                type="checkbox"
                id="confirmNotAbuse"
                name="notAbuse"
                label={
                  <span>
                    I acknowledge this is <strong>NOT</strong> to be abused and I will immediately switch back and restore my school access when done
                  </span>
                }
                checked={confirmations.notAbuse}
                onChange={handleConfirmationChange}
                disabled={isLoading}
              />
            </div>
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose} disabled={isLoading || success}>
          Cancel
        </Button>
        <Button
          variant="danger"
          onClick={handleSwitchSchool}
          disabled={!isFormValid || isLoading || success}
        >
          {isLoading ? "Switching..." : "Switch School Access"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default SwitchSchoolAccessModal;